import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Hourglass, ShieldCheck, ArrowDownToLine } from "lucide-react";
import { toast } from "sonner";
import { api, errText, money } from "../../lib/api";
import { Btn, Card, F, PageHeader } from "../../components/admin/ui";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";

const BUCKET = { in_progress: ["قيد التنفيذ", "bg-blue-50 text-blue-700"], pending: ["معلّقة", "bg-amber-50 text-amber-700"], earned: ["آمنة", "bg-green-50 text-green-700"] };
const d = (s) => (s ? new Date(s).toLocaleDateString("ar-SA") : "—");

const Box = ({ icon: Icon, label, value, hint, testid, tone }) => (
  <div className={`rounded-md border p-5 ${tone}`}>
    <div className="flex items-center gap-2 text-sm"><Icon size={17} />{label}</div>
    <p data-testid={testid} className="mt-3 text-3xl font-bold">{money(value)} <span className="text-sm font-normal">ر.س</span></p>
    <p className="mt-1 text-xs opacity-70">{hint}</p>
  </div>
);

export default function Finance() {
  const [f, setF] = useState(null);
  const [wd, setWd] = useState(null);
  const load = () => api.get("/admin/finance").then((r) => setF(r.data));
  useEffect(() => { load(); }, []);

  const withdraw = async () => {
    try {
      await api.post("/admin/finance/withdrawals", { amount: Number(wd.amount), note: wd.note });
      toast.success("تم تسجيل السحب");
      setWd(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail === "exceeds_available" ? "المبلغ أكبر من الرصيد الآمن للسحب" : errText(e));
    }
  };

  if (!f) return null;
  return (
    <div data-testid="admin-finance">
      <PageHeader title="الأرباح والمحفظة" desc={`الربح = المبيعات بعد الخصم وبدون الضريبة − تكلفة المنتجات. تبقى أرباح الطلب معلّقة ${f.return_days} أيام بعد التسليم (فترة الاسترجاع) ثم تنتقل تلقائيًا للرصيد الآمن.`}>
        <Btn data-testid="withdraw-button" onClick={() => setWd({ amount: f.available, note: "" })} disabled={f.available <= 0}><ArrowDownToLine size={16} />تسجيل سحب</Btn>
      </PageHeader>
      <div className="grid gap-4 md:grid-cols-3">
        <Box icon={Clock} label="أرباح قيد التنفيذ" value={f.in_progress} hint="طلبات لم تُسلَّم بعد (متوقعة)" testid="wallet-in-progress" tone="border-blue-100 bg-blue-50/50 text-blue-900" />
        <Box icon={Hourglass} label="أرباح معلّقة" value={f.pending} hint={`طلبات مُسلَّمة منذ أقل من ${f.return_days} أيام`} testid="wallet-pending" tone="border-amber-100 bg-amber-50/60 text-amber-900" />
        <Box icon={ShieldCheck} label="رصيد آمن للسحب" value={f.available} hint={`إجمالي الأرباح الآمنة ${money(f.earned)} − المسحوب ${money(f.withdrawn)}`} testid="wallet-available" tone="border-green-100 bg-green-50/60 text-green-900" />
      </div>
      <Card title="أرباح الطلبات" className="mt-4">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500"><tr className="text-right"><th className="p-2">الطلب</th><th>العميلة</th><th>التسليم</th><th>صافي المبيعات</th><th>التكلفة</th><th>الربح</th><th>الحالة</th><th>يصبح آمنًا في</th></tr></thead>
            <tbody>
              {f.orders.map((o) => (
                <tr key={o.order_number} data-testid={`finance-row-${o.order_number}`} className="border-t border-gray-100">
                  <td className="p-2"><Link to={`/admin/orders/${o.order_number}`} className="font-mono font-bold hover:underline">{o.order_number}</Link></td>
                  <td>{o.customer}</td><td className="text-xs">{d(o.delivered_at)}</td><td>{money(o.net_sales)}</td>
                  <td>{money(o.cost)}{o.missing_cost && <span className="mr-1 text-[10px] text-amber-600" title="منتج بدون تكلفة مسجلة">⚠ تكلفة ناقصة</span>}</td>
                  <td className={o.profit < 0 ? "text-red-600" : "font-medium"}>{money(o.profit)}</td>
                  <td><span className={`rounded px-2 py-0.5 text-xs ${BUCKET[o.bucket][1]}`}>{BUCKET[o.bucket][0]}</span></td>
                  <td className="text-xs">{d(o.release_at)}</td>
                </tr>
              ))}
              {!f.orders.length && <tr><td colSpan={8} className="p-8 text-center text-gray-500">لا توجد طلبات بعد</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
      <Card title="سجل السحوبات" className="mt-4">
        {!f.withdrawals.length && <p className="text-sm text-gray-500">لا توجد سحوبات</p>}
        {f.withdrawals.map((w) => (
          <div key={w.id} className="flex justify-between border-b border-gray-100 py-2 text-sm last:border-0">
            <span>{d(w.at)} · {w.by}{w.note && ` · ${w.note}`}</span><span className="font-bold">{money(w.amount)} ر.س</span>
          </div>
        ))}
      </Card>
      <Dialog open={!!wd} onOpenChange={(o) => !o && setWd(null)}>
        <DialogContent dir="rtl" className="max-w-sm">
          <DialogTitle className="pr-8">تسجيل سحب من الرصيد الآمن</DialogTitle>
          {wd && (
            <div className="space-y-3">
              <F label={`المبلغ (المتاح ${money(f.available)} ر.س)`} type="number" testid="withdraw-amount-input" value={wd.amount} onChange={(v) => setWd({ ...wd, amount: v })} />
              <F label="ملاحظة (مثال: تحويل بنكي)" value={wd.note} onChange={(v) => setWd({ ...wd, note: v })} />
              <p className="text-xs text-gray-500">هذا تسجيل محاسبي فقط ولا يحوّل أموالًا فعليًا.</p>
              <Btn data-testid="confirm-withdraw-button" className="w-full" onClick={withdraw} disabled={!(Number(wd.amount) > 0)}>تأكيد</Btn>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
